<script lang="ts" setup>
import { AxiosError } from 'axios';
import { useLocaleStore } from '@/stores/locale';
import { ref, reactive } from 'vue';
import { colors } from '@/data/colors';
import Swal from 'sweetalert2';
import { useI18n } from 'vue-i18n';
import axiosApiClient from '../../axios';

interface RegisterForm {
    name: string;
    email: string;
    password: string;
    password_confirmation: string;
    phone: string;
}
const form = reactive<RegisterForm>({
    name: '',
    email: '',
    password: '',
    password_confirmation: '',
    phone: '',
});
const errors = reactive({
    name: [],
    email: [],
    password: [],
    password_confirmation: [],
    phone: [],
});
const localeStore = useLocaleStore();
const isbusy = ref(false);
const t = useI18n().t;
const register = async (payload: RegisterForm) => {
    isbusy.value = true;
    await axiosApiClient.get('/sanctum/csrf-cookie').then(async () => {
        try {
            const response = await axiosApiClient.post('/register', payload);
            // Handle successful registration (e.g., redirect to login page)
            if (response.data.error != null) {
                Swal.fire({
                    icon: 'error',
                    title: t('Oops...'),
                    text: response?.data?.message || 'Something went wrong!',
                    // footer: '<a href="">Why do I have this issue?</a>',
                    showConfirmButton: false,
                    timer: 1500,
                    timerProgressBar: true,
                });
                isbusy.value = false;
                return;
            }
            Swal.fire({
                icon: 'success',
                title: t('Success!'),
                position: 'top-end',
                showClass: {
                    popup: 'animate__animated animate__fadeInDown',
                },
                hideClass: {
                    popup: 'animate__animated animate__fadeOutUp',
                },
                toast: true,
                text: response?.data?.message || t('Registration successful!'),
                // footer: '<a href="">Why do I have this issue?</a>',
                showConfirmButton: false,
                timer: 1500,
            }).then(() => {
                // Redirect to login page after successful registration
                window.location.href = '/login';
            });
        } catch (e) {

            if (e instanceof AxiosError && e.response?.status === 422) {
                errors.name = e.response.data.errors.name || [];
                errors.email = e.response.data.errors.email || [];
                errors.password = e.response.data.errors.password || [];
                errors.password_confirmation = e.response.data.errors.password_confirmation || [];
                errors.phone = e.response.data.errors.phone || [];
            }
            else {

                Swal.fire({
                    icon: 'error',
                    title: 'Oops...',
                    text: 'Something went wrong! \n\r' + e,
                    // footer: '<a href="">Why do I have this issue?</a>',
                    showConfirmButton: false,
                    timer: 3500,
                    timerProgressBar: true,
                });
            }
        }
        isbusy.value = false;
        // Reset form fields after submission
        form.name = '';
        form.email = '';
        form.password = '';
        form.password_confirmation = '';
        form.phone = '';


        const checkbox = document.querySelector('input[type="checkbox"]') as HTMLInputElement;
        if (checkbox) {
            checkbox.checked = false;
        }
    }).catch((error) => {
        console.error('Error setting CSRF cookie:', error);
    });

};



</script>
<template>
    <div :style="{ backgroundColor: colors.primary }" class="signup-container">
        <div class="signup-form" :dir="localeStore.locale === 'ar' ? 'rtl' : 'ltr'">
            <form method="post" @submit.prevent="register(form)">
                <div class="text-center mb-4">
                    <img alt="logo" src="../../assets/img/logo.png" class="logo" />
                </div>
                <h2>{{ t('Register') }}</h2>
                <p class="hint-text">{{ t('Create your account. It\'s free and only takes a minute.') }}</p>
                <div class="form-group">
                    <div class="row">
                        <div class="col">
                            <input type="text" class="form-control" v-model="form.name" name="name"
                                :placeholder="t('Full Name')" required>
                            <div v-if="errors.name.length > 0">
                                <span class="text-danger" v-for="(error, index) in errors.name" :key="index">
                                    {{ error }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="form-group">
                    <input type="email" class="form-control" v-model="form.email" name="email" :placeholder="t('Email')"
                        required>
                    <div v-if="errors.email.length > 0">
                        <span class="text-danger" v-for="(error, index) in errors.email" :key="index">
                            {{ error }}
                        </span>
                    </div>
                </div>
                <div class="form-group">
                    <input type="password" class="form-control" v-model="form.password" name="password"
                        :placeholder="t('Password')" required>
                    <div v-if="errors.password.length > 0">
                        <span class="text-danger" v-for="(error, index) in errors.password" :key="index">
                            {{ error }}
                        </span>
                    </div>
                </div>
                <div class="form-group">
                    <input type="password" class="form-control" v-model="form.password_confirmation"
                        name="password_confirmation" :placeholder="t('Confirm Password')" required>
                    <div v-if="errors.password_confirmation.length > 0">
                        <span class="text-danger" v-for="(error, index) in errors.password_confirmation" :key="index">
                            {{ error }}
                        </span>
                    </div>
                </div>
                <div class="form-group">
                    <div class="row">
                        <div class="col"><input type="text" class="form-control" v-model="form.phone" name="phone"
                                :placeholder="t('Phone Number')" required>
                            <div v-if="errors.phone.length > 0">
                                <span class="text-danger" v-for="(error, index) in errors.phone" :key="index">
                                    {{ error }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="d-flex gap-1 my-2">
                    <input type="checkbox" id="accept_policy" class="input-checkbox" required>
                    <label for="accept_policy" class="form-check-label">
                        {{ t('I accept the') }} <a href="#">{{ t('Terms of Use') }}</a> &amp; <a href="#">{{
                            t('Privacy Policy')
                        }}</a></label>
                </div>
                <div class="form-group">
                    <button type="submit" class="btn btn-success btn-lg btn-block">{{ t('Register') }}
                        <loading-spinner :is-loading="isbusy" />
                    </button>
                </div>
                <div class="text-center"> {{ t('Already have an account?') }} <a href="/login">{{ t('Sign in')
                        }}</a>
                </div>
            </form>
        </div>
    </div>

</template>


<style lang="scss" scoped>
:root {
    --primary-color: colors.primary;
    --primary-color-light: colors.primaryLight;
    --primary-color-dark: colors.primaryDark;
}

.signup-container {
    width: 100%;
}

.logo {
    width: 270px;
    height: auto;
}

.form-control {
    height: 40px;
    box-shadow: none;
    color: #969fa4;
}

.form-control:focus {
    border-color: #5cb85c;
}

.form-control,
.btn {
    border-radius: 3px;
}

.signup-form {
    width: 90%;
    max-width: 450px;
    margin: 0 auto;
    padding: 30px 0;
    font-size: 15px;
}

@media (min-width: 768px) {
    .signup-form {
        width: 70%;
    }
}

@media (min-width: 1024px) {
    .signup-form {
        width: 450px;
    }
}

.signup-form h2 {
    color: var(--primary-color);
    margin: 0 0 15px;
    position: relative;
    text-align: center;
}

.signup-form h2:before,
.signup-form h2:after {
    content: "";
    height: 2px;
    width: 30%;
    background: #d4d4d4;
    position: absolute;
    top: 50%;
    z-index: 2;
}

.signup-form h2:before {
    left: 0;
}

.signup-form h2:after {
    right: 0;
}

.signup-form .hint-text {
    color: #999;
    margin-bottom: 30px;
    text-align: center;
}

.signup-form form {
    color: #999;
    border-radius: 3px;
    margin-bottom: 15px;
    background: #f2f3f7;
    box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.3);
    padding: 30px;
}

.signup-form .form-group {
    margin-bottom: 20px;
}

.signup-form input[type="checkbox"] {
    margin-top: 3px;
}

.signup-form .btn {
    font-size: 16px;
    font-weight: bold;
    min-width: 140px;
    outline: none !important;
}

.signup-form .row div:first-child {
    padding-right: 10px;
}

.signup-form .row div:last-child {
    padding-left: 10px;
}

.signup-form a {
    text-decoration: underline;
}

.signup-form a:hover {
    text-decoration: none;
}

.signup-form form a {
    color: var(--primary-color);
    text-decoration: none;
}

.signup-form form a:hover {
    text-decoration: underline;
}
</style>